import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { computePosition, autoUpdate, offset, flip, shift } from '@floating-ui/dom';
import type { Editor } from '@tiptap/core';
import { createLinkedTextInsertContent } from '../extensions/tiptapShared';
import styles from './Toolbar.module.css';

export type CaretLinkPopoverProps = {
  editor: Editor;
  pos: number;
  initialLabel?: string;
  containerRef?: React.RefObject<HTMLElement | null>;
  onClose: () => void;
};

export const CaretLinkPopover = ({
  editor,
  pos,
  initialLabel = '',
  containerRef,
  onClose
}: CaretLinkPopoverProps) => {
  const [url, setUrl] = useState('https://');
  const [label, setLabel] = useState(initialLabel);
  const panelRef = useRef<HTMLDivElement>(null);
  const urlInputRef = useRef<HTMLInputElement>(null);

  const virtualReference = useMemo(
    () => ({
      getBoundingClientRect: () => {
        if (!editor.view) return new DOMRect(0, 0, 0, 0);
        const coords = editor.view.coordsAtPos(pos);
        return new DOMRect(coords.left, coords.top, 0, coords.bottom - coords.top);
      }
    }),
    [editor, pos]
  );

  useLayoutEffect(() => {
    const floating = panelRef.current;
    if (!floating) return;
    const boundary = containerRef?.current ?? undefined;
    const updatePosition = () => {
      computePosition(virtualReference, floating, {
        placement: 'bottom-start',
        strategy: 'fixed',
        middleware: [offset(6), flip({ padding: 8, boundary }), shift({ padding: 8, boundary })]
      }).then(({ x, y, strategy }) => {
        Object.assign(floating.style, { position: strategy, left: `${x}px`, top: `${y}px`, zIndex: '100000' });
      });
    };
    updatePosition();
    return autoUpdate(virtualReference, floating, updatePosition, {
      ancestorScroll: true,
      ancestorResize: true,
      elementResize: true,
      layoutShift: true
    });
  }, [virtualReference, containerRef]);

  useEffect(() => {
    urlInputRef.current?.focus();
    urlInputRef.current?.setSelectionRange(url.length, url.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const isInsidePanel = (e: Event) => {
      const root = panelRef.current;
      if (!root) return false;
      const t = e.target as Node | null;
      return !!t && root.contains(t);
    };
    const onDocDown = (e: MouseEvent) => {
      if (!isInsidePanel(e)) onClose();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  const apply = () => {
    const href = url.trim();
    if (!href) {
      onClose();
      return;
    }
    const displayText = (label.trim() || href).replace(/\s+/g, ' ');
    editor
      .chain()
      .focus()
      .setTextSelection(pos)
      .insertContent(createLinkedTextInsertContent(href, displayText))
      .run();
    onClose();
  };

  return (
    <div
      ref={panelRef}
      className={`${styles.popoverSurface} ${styles.popoverSurfaceRaised} ${styles.linkPanel}`}
      style={{ position: 'fixed' }}
      onMouseDown={e => e.stopPropagation()}
      onPointerDown={e => e.stopPropagation()}
    >
      <label className={styles.linkField}>
        <span>URL</span>
        <input
          ref={urlInputRef}
          type="url"
          className={styles.linkInput}
          value={url}
          onChange={e => setUrl(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              apply();
            }
          }}
          placeholder="https://"
          autoComplete="off"
        />
      </label>
      <label className={styles.linkField}>
        <span>Link text</span>
        <input
          type="text"
          className={styles.linkInput}
          value={label}
          onChange={e => setLabel(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              apply();
            }
          }}
          placeholder="Visible label"
          autoComplete="off"
        />
      </label>
      <div className={styles.linkActions}>
        <button type="button" className={styles.linkApplyBtn} onClick={apply}>
          Apply
        </button>
      </div>
    </div>
  );
};

export default CaretLinkPopover;
